// Order lifecycle helpers shared by the order list and the order page.
// `order.state` (ProjectKartApi Modules/Ordering/OrderState.cs) is the source of truth;
// the `status` array is only a human-readable history.

export const ORDER_STATE = {
  PENDING_PAYMENT: 'PendingPayment',
  PAYMENT_SUBMITTED: 'PaymentSubmitted',
  PAID: 'Paid',
  FULFILLED: 'Fulfilled',
  PAYMENT_FAILED: 'PaymentFailed',
  CANCELLED: 'Cancelled',
  REFUNDED: 'Refunded',
};

const STATE_META = new Map([
  ['PendingPayment', { label: 'Awaiting payment', tone: 'warning' }],
  ['PaymentSubmitted', { label: 'Payment under review', tone: 'info' }],
  ['Paid', { label: 'Paid', tone: 'success' }],
  ['Fulfilled', { label: 'Delivered', tone: 'success' }],
  ['PaymentFailed', { label: 'Payment failed', tone: 'danger' }],
  ['Cancelled', { label: 'Cancelled', tone: undefined }],
  ['Refunded', { label: 'Refunded', tone: undefined }],
]);

// Same mapping as OrderStateMachine.ParseStatusTitle on the server.
const TITLE_TO_STATE = new Map([
  ['PENDING', 'PendingPayment'],
  ['PAYMENT_PENDING', 'PendingPayment'],
  ['PENDING_PAYMENT', 'PendingPayment'],
  ['PAYMENT_SUBMITED', 'PaymentSubmitted'],
  ['PAYMENT_SUBMITTED', 'PaymentSubmitted'],
  ['SUBMITTED', 'PaymentSubmitted'],
  ['PAYMENT_COMPLETED', 'Paid'],
  ['COMPLETED', 'Paid'],
  ['PAID', 'Paid'],
  ['SUCCESS', 'Paid'],
  ['CONFIRMED', 'Paid'],
  ['DELIVERED', 'Fulfilled'],
  ['FULFILLED', 'Fulfilled'],
  ['PAYMENT_FAILED', 'PaymentFailed'],
  ['FAILED', 'PaymentFailed'],
  ['REJECTED', 'PaymentFailed'],
  ['CANCELLED', 'Cancelled'],
  ['CANCELED', 'Cancelled'],
  ['REFUNDED', 'Refunded'],
]);

const normalizeTitle = title =>
  String(title || '')
    .trim()
    .toUpperCase()
    .replace(/ /g, '_');

const historyTitles = order =>
  Array.isArray(order?.status) ? order.status.map(s => normalizeTitle(s?.title)) : [];

/**
 * The order's lifecycle state. Orders listed by `my-orders` may predate the `state` field,
 * so this falls back to the same rules the server uses (OrderStateMachine.FromLegacy).
 */
export const resolveOrderState = order => {
  if (!order) return ORDER_STATE.PENDING_PAYMENT;
  if (order.state && STATE_META.has(order.state)) return order.state;

  const titles = historyTitles(order);
  const last = titles[titles.length - 1];
  if (last === 'REFUNDED') return ORDER_STATE.REFUNDED;

  if (order.isFullPaymentDone) {
    const delivered =
      titles.some(t => t === 'DELIVERED' || t === 'COMPLETED') ||
      (order.digitalDeliverables?.length ?? 0) > 0;
    return delivered ? ORDER_STATE.FULFILLED : ORDER_STATE.PAID;
  }

  const parsed = TITLE_TO_STATE.get(last);
  if (
    parsed === ORDER_STATE.CANCELLED ||
    parsed === ORDER_STATE.PAYMENT_FAILED ||
    parsed === ORDER_STATE.PAYMENT_SUBMITTED
  ) {
    return parsed;
  }
  return ORDER_STATE.PENDING_PAYMENT;
};

export const orderStateMeta = state => STATE_META.get(state) || { label: state || 'Unknown' };

export const isPaidState = state =>
  state === ORDER_STATE.PAID || state === ORDER_STATE.FULFILLED;

export const needsPayment = state =>
  state === ORDER_STATE.PENDING_PAYMENT || state === ORDER_STATE.PAYMENT_FAILED;

// When a history entry with one of these titles was recorded (for the timeline).
export const historyDate = (order, ...titles) => {
  if (!Array.isArray(order?.status)) return null;
  const wanted = titles.map(normalizeTitle);
  const match = [...order.status].reverse().find(s => wanted.includes(normalizeTitle(s?.title)));
  return match?.createAt || null;
};

// The customer-facing order number (e.g. "PK-…"); falls back to the database id.
export const orderNumber = order => order?.orderId || order?.id || '';

// "Project name" or "Project name + 2 more"
export const orderTitle = order => {
  const items = order?.items || [];
  if (!items.length) return 'Order';
  const first = items[0]?.productName || 'Project';
  return items.length > 1 ? `${first} + ${items.length - 1} more` : first;
};
